import { differenceInDays } from "date-fns";
import { object, string } from "yup";
import type { ChildCoveredPerson, ChildQuote } from "@/api_client";
import type { ChildEntryContent } from "@/services/cms/contentTypes/entry/ChildEntryContent";
import { getErrorMessageNew } from "@/utils/errorMessage";
import { AgeValidator } from "@/validators/shared/ageValidators";
import { FinnishSSN } from "@/validators/shared/ssnValidator";

const validateSSN = (ssn: string) => {
  const finnishSSN = new FinnishSSN(ssn);
  return finnishSSN.validateSSN();
};

export type ChildEntryFormType = Pick<
  ChildQuote,
  "coveredPerson" | "policyHolderSsn"
>;

export const ResolveChildEntry = (
  content: ChildEntryContent | undefined | null,
) =>
  object<ChildEntryFormType>({
    coveredPerson: buildCoveredPersonSchema(content).required(),
    policyHolderSsn: string()
      .required(
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "valid-ssn",
        ) ?? "",
        validateSSN,
      )
      .test(
        "is-18+",
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "is-18+",
        ) ?? "",
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          return ageValidator.isAtLeast18;
        },
      ),
  });

const buildCoveredPersonSchema = (
  content: ChildEntryContent | undefined | null,
) =>
  object<Pick<ChildCoveredPerson, "ssn">>({
    ssn: string()
      .required(
        getErrorMessageNew(content?.childSsn?.ValidationMessages, "required"),
      )
      .test(
        "valid-ssn",
        getErrorMessageNew(content?.childSsn?.ValidationMessages, "valid-ssn"),
        validateSSN,
      )
      .test(
        "is-too-old",
        getErrorMessageNew(content?.childSsn?.ValidationMessages, "is-too-old"),
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          if (ageValidator.age) {
            return ageValidator.age < 15;
          }
          return true;
        },
      )
      .test(
        "is-too-young",
        getErrorMessageNew(
          content?.childSsn?.ValidationMessages,
          "is-too-young",
        ),
        (ssn) => {
          const ageValidator = new AgeValidator(ssn);
          if (ageValidator.birthDate) {
            const days = differenceInDays(new Date(), ageValidator.birthDate);

            return days >= 7;
          }
          return true;
        },
      ),
  });
